import { deleteCookie, getCookie, getLocalStorage, removeLocalStorage, restoreCookieFromLocalStorage, setLocalStorage, } from '../util/storage';
import { MultiWindowEmitter } from '../util/event';
import debug, { DEBUG, INFO, WARN } from '../util/debug';
import { searchParams } from '../util/location';
import { getConsentData } from '../util/consent-string';
import { ConsentString } from 'consent-string';
const CMP_META_KEY = 'cmp-meta';
const CMP_META_CURRENT_VERSION = 2;
const VERSION_TO_UPGRADE_SAMESITE = 2;
const getRootWindowDomain = () => {
    const match = (window.location !== window.parent.location
        ? document.referrer
        : document.location.href).match(/https?:\/\/([^:\/\n]+)/im);
    return match ? match[1] : undefined;
};
export default class ConsentModel extends MultiWindowEmitter {
    constructor(backendBaseUrl, vendorList, clientConsentString, geoInfo, legislation) {
        super();
        this.backendBaseUrl = backendBaseUrl;
        this.vendorList = vendorList;
        this.clientConsentString = clientConsentString;
        this.geoInfo = geoInfo;
        this.legislation = legislation;
        this.unload = () => {
            deleteCookie(ConsentModel.getCookieName());
            deleteCookie('uuid');
            removeLocalStorage(ConsentModel.getCookieName());
            removeLocalStorage(`${ConsentModel.getCookieName()}-expires`);
            removeLocalStorage('uuid');
            removeLocalStorage('uuid-expires');
        };
        this.metadata = clientConsentString || '';
        this.consented = /^1.N.$/.test(this.metadata);
        this.on('update', () => this.load());
        this.on('destroy', this.unload);
    }
    static getCookieName() {
        return 'usprivacy';
    }
    static async load(backendBaseUrl, clientConsentString, geoInfo, legislation, pVendorList) {
        const [vendorList] = await Promise.all([pVendorList]);
        debug(`creating consentModel, clientConsentString:" ${clientConsentString}`);
        const consentModel = new this(backendBaseUrl, vendorList, clientConsentString, geoInfo, legislation);
        if (!clientConsentString) {
            await consentModel.load();
        }
        return consentModel;
    }
    get daisybit() {
        if (this.metadata) {
            return this.metadata;
        }
        return getConsentData(this.vendorList).getConsentString();
    }
    get USPString() {
        return this.metadata;
    }
    load() {
        this.metadata = getCookie(ConsentModel.getCookieName());
        if (!this.metadata) {
            // No cookie? Check for a consent token in localStorage and restore
            const lsMetaData = restoreCookieFromLocalStorage(ConsentModel.getCookieName()) &&
                restoreCookieFromLocalStorage('uuid') &&
                getLocalStorage(ConsentModel.getCookieName());
            if (lsMetaData) {
                this.metadata = lsMetaData;
            }
        }
        debug(`loading metadata "${this.metadata}"`, DEBUG);
        this.consented = /^1.N.$/.test(this.metadata);
    }
    hasChoiceBeenMade() {
        const usprivacy = getCookie('usprivacy') || getLocalStorage('usprivacy');
        return /^1.[YN].$/.test(usprivacy);
    }
    getAllVendorIds() {
        return this.vendorList.vendors.map(({ id }) => id.toString());
    }
    getConsentVersion() {
        return this.consentData.getVersion();
    }
    getConsentData(version) {
        if (typeof version !== 'undefined' &&
            version !== this.consentData.getVersion()) {
            return null;
        }
        return this.metadata;
    }
    getCookieExpireDays(consented) {
        const consentExpires = 13 * 30; // 13 months
        const noConsentExpires = 30; // 1 month
        return consented ? consentExpires : noConsentExpires;
    }
    updateStorage(consentStr, uuid) {
        const consentExdays = this.getCookieExpireDays(this.consented);
        const consentDate = new Date();
        consentDate.setTime(consentDate.getTime() + consentExdays * 24 * 60 * 60 * 1000);
        setLocalStorage(ConsentModel.getCookieName(), consentStr);
        setLocalStorage(`${ConsentModel.getCookieName()}-expires`, consentDate.toUTCString());
        const uuidExDays = 100 * 365; // 100 years
        const uuidDate = new Date();
        uuidDate.setTime(uuidDate.getTime() + uuidExDays * 24 * 60 * 60 * 1000);
        setLocalStorage('uuid', uuid);
        setLocalStorage(`uuid-expires`, uuidDate.toUTCString());
        const meta = this.getMetaFromLocalStorage();
        meta.version = CMP_META_CURRENT_VERSION;
        setLocalStorage(CMP_META_KEY, JSON.stringify(meta));
    }
    async save(origin) {
        const res = await this.send(origin);
        const { euconsent, usprivacy, uuid } = await res.json();
        const consentStr = euconsent || usprivacy;
        this.updateStorage(consentStr, uuid);
        // set version
        this.load();
        this.emit('update');
    }
    destroy() {
        deleteCookie(ConsentModel.getCookieName());
        deleteCookie('uuid');
        removeLocalStorage(ConsentModel.getCookieName());
        removeLocalStorage(`${ConsentModel.getCookieName()}-expires`);
        removeLocalStorage('uuid');
        removeLocalStorage('uuid-expires');
        this.emit('destroy');
    }
    getMetaFromLocalStorage() {
        const storage = localStorage.getItem(CMP_META_KEY);
        return storage ? JSON.parse(storage) : { version: 0 };
    }
    async upgradeConsent() {
        const bodyOptions = [];
        const { version } = this.getMetaFromLocalStorage();
        if (version < VERSION_TO_UPGRADE_SAMESITE) {
            bodyOptions.push({});
        }
        // if (this.consentData.getCmpId() === 1) {
        //   debug('added item to post for cmpId')
        //
        //   let chance = parseFloat(localStorage.getItem('cmp-chance-of-upgrade') || '-1')
        //   if (chance === -1) {
        //     chance = Math.random()
        //     localStorage.setItem('cmp-chance-of-upgrade', '' + chance)
        //   }
        //
        //   const rollOutStart = Date.parse('2019-06-03T10:00:00')
        //   const rollOutEnd = Date.parse('2019-06-06T16:00:00')
        //   const rollOutProgress = (+new Date() - rollOutStart) / (rollOutEnd - rollOutStart)
        //   if (rollOutProgress > chance) {
        //     bodyOptions.push({
        //       consentStringLastUpdated: '2020-01-01T00:00:00.000Z'
        //     })
        //   }
        // }
        if (bodyOptions.length === 0) {
            return;
        }
        try {
            const body = {
                domain: getRootWindowDomain(),
                ...bodyOptions.reduce((acc, next) => ({ ...acc, ...next }), {}),
            };
            await this.postUpgrade(body);
            const metadata = getCookie(ConsentModel.cookieName);
            const consentData = new ConsentString(metadata);
            debug(`New vendor list version: ${consentData.getVendorListVersion()}`, INFO);
        }
        catch (e) {
            debug(`Error trying to upgrade consent: ${e}`, WARN);
        }
    }
    async setCCPANotApplicableStatus([origin, channel]) {
        const requestUUID = getLocalStorage('uuid') || getCookie('uuid') || undefined;
        const body = {
            channel,
            domain: origin,
            ...{ uuid: requestUUID },
        };
        const knownPrivacyString = getLocalStorage(ConsentModel.getCookieName());
        const knownUuid = getLocalStorage('uuid');
        const shouldUpdateServer = knownPrivacyString !== '1---' || !knownUuid;
        if (!shouldUpdateServer) {
            return;
        }
        const rp = fetch(`${this.backendBaseUrl}/ccpa/consent`, {
            body: JSON.stringify(body),
            credentials: 'include',
            headers: {
                'content-type': 'application/json',
            },
            method: 'POST',
        }).then((r) => r.json());
        const { usprivacy, uuid } = await rp;
        this.updateStorage(usprivacy, uuid);
        this.emit('update');
    }
    send(origin) {
        const channel = searchParams['mol.ads.cmp.channel'] || null;
        const uuid = getLocalStorage('uuid') || getCookie('uuid') || undefined;
        const reqObj = {
            legislation: this.legislation,
            notice: true,
            optOut: Boolean(!this.consented),
            lspa: false,
            channel,
            domain: origin,
            uuid,
            country: this.geoInfo.geo,
            region: this.geoInfo.region,
        };
        return fetch(`${this.backendBaseUrl}/ccpa/consent`, {
            body: JSON.stringify(reqObj),
            credentials: 'include',
            headers: {
                'content-type': 'application/json',
            },
            method: 'POST',
        });
    }
    async postUpgrade(bodyOptions) {
        const rp = await fetch(`${this.backendBaseUrl}/ccpa/consent/upgrade`, {
            body: JSON.stringify({
                ...bodyOptions,
                usprivacy: getCookie(ConsentModel.getCookieName()),
                uuid: getCookie('uuid'),
            }),
            credentials: 'include',
            headers: {
                'content-type': 'application/json',
            },
            method: 'POST',
        });
        const { usprivacy, uuid } = await rp.json();
        this.updateStorage(usprivacy, uuid);
    }
}
ConsentModel.cookieName = 'euconsent';
